import { Injectable, inject } from "@angular/core";
import { CategoriesService, HelpersService, MappingOfStatusesService } from "@shared/index";
import { JobsBillingCard, SupportedLanguage, TaxprepT1Card } from "@app/views-interfaces";
import { Option, SingleSelectModel, VisibilityParams } from "./filter-types";
import { CardDisplayService } from "@shared/card-display";
import { JobsAndBillingJobTypesService } from "@shared/jobs-and-billing-job-types-service";
import { compareString } from "@shared/utils";

@Injectable({
    providedIn: "root",
})
export class SortGeneratorService {
    private readonly categoriesService = inject(CategoriesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly helpersService = inject(HelpersService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);

    constructor() {}

    generateSortByModel(
        selection: Option | null,
        viewType: string | null,
        isiFirmTaxDashboard: boolean,
        dashboardTaxYear: number = 0,
    ): SingleSelectModel {
        const resolvedViewType = viewType ?? "all";

        const isCompletedOrAssigneeVisible: Option["isVisible"] = (params) => {
            const visibilityParams = params as VisibilityParams;
            return visibilityParams.viewType === "completed" || visibilityParams.viewType === "assignee";
        };
        const isDateReceivedVisible: Option["isVisible"] = (params) => {
            const visibilityParams = params as VisibilityParams;
            return !visibilityParams.isiFirmTaxDashboard || visibilityParams.dashboardTaxYear > 2024;
        };

        // Define priority options that must appear first
        const priorityOptions: Option[] = [
            {
                text: "FiltersSidebar.Options.SortBy.FirstName",
                value: "FirstName",
            },
            {
                text: "FiltersSidebar.Options.SortBy.LastName",
                value: "LastName",
            },
        ];

        const otherOptions: Option[] = [
            {
                text: "FiltersSidebar.Options.SortBy.DaysInStep",
                value: "DaysInStep",
            },
            {
                text: "FiltersSidebar.Options.SortBy.Deadline",
                value: "Deadline",
            },
            {
                text: "FiltersSidebar.Options.SortBy.Category",
                value: "Category",
            },
            {
                text: "FiltersSidebar.Options.SortBy.DateReceivedThisYear",
                value: "DateReceivedThisYear",
                isVisible: isDateReceivedVisible,
            },
            {
                text: "FiltersSidebar.Options.SortBy.DateReceived",
                value: "DateReceived",
                isVisible: isDateReceivedVisible,
            },
            {
                text: "FiltersSidebar.Options.SortBy.EndDate",
                value: "EndDate",
                isVisible: isCompletedOrAssigneeVisible,
            },
            {
                text: "FiltersSidebar.Options.SortBy.EndDateLastYear",
                value: "EndDateLastYear",
                isVisible: isCompletedOrAssigneeVisible,
            },
        ];

        // Combine priority options first, then view-specific ordered options.
        const options: Option[] = [...priorityOptions, ...otherOptions];

        const visibilityParams: VisibilityParams = {
            viewType: resolvedViewType,
            isiFirmTaxDashboard,
            dashboardTaxYear,
        };
        const applicableOptions = options.filter((option) => this.resolveOptionVisibility(option, visibilityParams));
        const selectedValue = selection?.value;
        if (
            !selectedValue ||
            selectedValue === "" ||
            !applicableOptions.some((option) => option.value === selectedValue && this.resolveOptionVisibility(option, visibilityParams))
        ) {
            selection = applicableOptions.find((option) => option.value === "LastName") || applicableOptions[0] || options[0] || selection;
        }
        const resolvedSelection = selection ?? applicableOptions[0] ?? options[0];

        return {
            label: "FiltersSidebar.Labels.SortBy",
            allOptions: options,
            applicableOptions,
            selection: resolvedSelection,
        };
    }

    private resolveOptionVisibility(option: Option, visibilityParams: VisibilityParams): boolean {
        if (option.isVisible == null) return true;
        if (typeof option.isVisible === "boolean") return option.isVisible;
        return option.isVisible(visibilityParams);
    }

    generateJobsBillingSortByModel(selection: Option | null, viewType: string | null): SingleSelectModel {
        const commonOptions: Option[] = [
            {
                text: "FiltersSidebar.Options.SortBy.FirstNameClientName",
                value: "FirstName",
            },
            {
                text: "FiltersSidebar.Options.SortBy.LastName",
                value: "LastName",
            },
            {
                text: "FiltersSidebar.Options.SortBy.DateReceivedTracking",
                value: "DateReceived",
            },
            {
                text: "FiltersSidebar.Options.SortBy.DaysInStep",
                value: "DaysInStep",
            },
            {
                text: "FiltersSidebar.Options.SortBy.JobType",
                value: "JobType",
            },
            {
                text: "FiltersSidebar.Options.SortBy.PeriodEnded",
                value: "PeriodEnded",
            },
            {
                text: "FiltersSidebar.Options.SortBy.Priority",
                value: "Priority",
            },
            {
                text: "FiltersSidebar.Options.SortBy.TargetEndDate",
                value: "TargetEndDate",
            },
            {
                text: "FiltersSidebar.Options.SortBy.TargetStartDate",
                value: "TargetStartDate",
            },
        ];
        const options =
            viewType === "completed"
                ? [
                      commonOptions[0],
                      commonOptions[1],
                      {
                          text: "FiltersSidebar.Options.SortBy.CompletionDate",
                          value: "CompletionDate",
                      },
                      ...commonOptions.slice(2),
                  ]
                : commonOptions;
        const defaultSelection = options.find((option) => option.value === "LastName") ?? options[0];
        const selectedValue = selection?.value;

        if (!selectedValue || selectedValue === "" || !options.some((option) => option.value === selectedValue)) {
            selection = defaultSelection;
        }

        return {
            label: "FiltersSidebar.Labels.SortBy",
            allOptions: options,
            applicableOptions: options,
            selection: selection ?? defaultSelection,
        };
    }

    generateSortOrderModel(selection: Option | null): SingleSelectModel {
        const options: Option[] = [
            {
                text: "FiltersSidebar.Options.Order.Ascending",
                value: "Ascending",
            },
            {
                text: "FiltersSidebar.Options.Order.Descending",
                value: "Descending",
            },
        ];
        if (!selection?.value || selection.value === "") {
            selection = options[0];
        }
        const resolvedSelection = selection ?? options[0];
        return {
            label: "FiltersSidebar.Labels.Order",
            allOptions: options,
            applicableOptions: options,
            selection: resolvedSelection,
        };
    }

    async generateSortBy(
        sortByValue: string | null,
        sortOrderValue: string | null,
    ): Promise<((a: TaxprepT1Card, b: TaxprepT1Card) => number) | null> {
        if ((!sortByValue || sortByValue === "") && (!sortOrderValue || sortOrderValue === "")) {
            return null;
        }

        const normalizedSortByValue = sortByValue ?? "";
        const normalizedSortOrderValue = sortOrderValue ?? "Ascending";
        const dir = normalizedSortOrderValue === "Ascending" ? "asc" : "desc";
        let sortFunction: (a: TaxprepT1Card, b: TaxprepT1Card) => number;

        if (normalizedSortByValue === "FirstName") {
            sortFunction = (a: TaxprepT1Card, b: TaxprepT1Card) => {
                const aContactFirstName = a.contactInformation.firstName ?? "";
                const bContactFirstName = b.contactInformation.firstName ?? "";
                const primaryComparison = compareString(aContactFirstName, bContactFirstName, dir);
                return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b, dir) : primaryComparison;
            };
        } else if (normalizedSortByValue === "LastName") {
            sortFunction = (a: TaxprepT1Card, b: TaxprepT1Card) => {
                const aContactLastName = a.contactInformation.lastName ?? "";
                const bContactLastName = b.contactInformation.lastName ?? "";
                const primaryComparison = compareString(aContactLastName, bContactLastName, dir);
                return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
            };
        } else if (normalizedSortByValue === "Category") {
            sortFunction =
                normalizedSortOrderValue === "Ascending"
                    ? (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aCategory = this.cardDisplayService.calculateCategoryForCard(a);
                          const bCategory = this.cardDisplayService.calculateCategoryForCard(b);
                          if (aCategory == null && bCategory == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aCategory == null) return -1;
                          if (bCategory == null) return 1;
                          const primaryComparison = this.categoriesService.sortByOrder(aCategory, bCategory);
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      }
                    : (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aCategory = this.cardDisplayService.calculateCategoryForCard(a);
                          const bCategory = this.cardDisplayService.calculateCategoryForCard(b);
                          if (aCategory == null && bCategory == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aCategory == null) return 1;
                          if (bCategory == null) return -1;
                          const primaryComparison = this.categoriesService.sortByOrder(bCategory, aCategory);
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      };
        } else if (normalizedSortByValue === "DaysInStep") {
            sortFunction =
                normalizedSortOrderValue === "Ascending"
                    ? (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return -1;
                          if (bDaysSince == null) return 1;
                          const primaryComparison = aDaysSince.secondsSinceInStep - bDaysSince.secondsSinceInStep;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      }
                    : (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return 1;
                          if (bDaysSince == null) return -1;
                          const primaryComparison = bDaysSince.secondsSinceInStep - aDaysSince.secondsSinceInStep;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      };
        } else if (normalizedSortByValue === "Deadline") {
            sortFunction =
                normalizedSortOrderValue === "Ascending"
                    ? (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const deadlineDateA = a.dateNeeded ? a.dateNeeded : (a.deadLine ?? 0);
                          const deadlineDateB = b.dateNeeded ? b.dateNeeded : (b.deadLine ?? 0);
                          const primaryComparison = deadlineDateA - deadlineDateB;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      }
                    : (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const deadlineDateA = a.dateNeeded ? a.dateNeeded : (a.deadLine ?? 0);
                          const deadlineDateB = b.dateNeeded ? b.dateNeeded : (b.deadLine ?? 0);
                          const primaryComparison = deadlineDateB - deadlineDateA;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      };
        } else if (normalizedSortByValue === "DateReceived") {
            sortFunction = this.createDateSortFunction<TaxprepT1Card>(
                (card) => card.lastYearDocumentsReceivedDate,
                normalizedSortOrderValue,
                (a, b) => this.compareT1FirstNamesAscending(a, b),
            );
        } else if (normalizedSortByValue === "DateReceivedThisYear") {
            sortFunction = this.createDateSortFunction<TaxprepT1Card>(
                (card) => card.documentsReceivedDate,
                normalizedSortOrderValue,
                (a, b) => this.compareT1FirstNamesAscending(a, b),
            );
        } else if (normalizedSortByValue === "EndDate") {
            sortFunction =
                normalizedSortOrderValue === "Ascending"
                    ? (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return -1;
                          if (bDaysSince == null) return 1;
                          const primaryComparison = aDaysSince.thisYearCompletionDateTimestamp - bDaysSince.thisYearCompletionDateTimestamp;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      }
                    : (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return 1;
                          if (bDaysSince == null) return -1;
                          const primaryComparison = bDaysSince.thisYearCompletionDateTimestamp - aDaysSince.thisYearCompletionDateTimestamp;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      };
        } else if (normalizedSortByValue === "EndDateLastYear") {
            sortFunction =
                normalizedSortOrderValue === "Ascending"
                    ? (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return -1;
                          if (bDaysSince == null) return 1;
                          const primaryComparison = aDaysSince.lastYearCompletedDateTimestamp - bDaysSince.lastYearCompletedDateTimestamp;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      }
                    : (a: TaxprepT1Card, b: TaxprepT1Card) => {
                          const aDaysSince = this.getDaysSince(a);
                          const bDaysSince = this.getDaysSince(b);
                          if (aDaysSince == null && bDaysSince == null) return this.compareT1FirstNamesAscending(a, b);
                          if (aDaysSince == null) return 1;
                          if (bDaysSince == null) return -1;
                          const primaryComparison = bDaysSince.lastYearCompletedDateTimestamp - aDaysSince.lastYearCompletedDateTimestamp;
                          return primaryComparison === 0 ? this.compareT1FirstNamesAscending(a, b) : primaryComparison;
                      };
        } else {
            throw new Error(`Sort value is not recognized : ${normalizedSortByValue}`);
        }

        return sortFunction;
    }

    async generateJobsBillingSortBy(
        sortByValue: string | null,
        sortOrderValue: string | null,
    ): Promise<((a: JobsBillingCard, b: JobsBillingCard) => number) | null> {
        if ((!sortByValue || sortByValue === "") && (!sortOrderValue || sortOrderValue === "")) {
            return null;
        }

        const normalizedSortByValue = sortByValue ?? "";
        const normalizedSortOrderValue = sortOrderValue ?? "Ascending";
        const dir = normalizedSortOrderValue === "Ascending" ? "asc" : "desc";
        let sortFunction: (a: JobsBillingCard, b: JobsBillingCard) => number;

        switch (normalizedSortByValue) {
            case "FirstName":
                sortFunction = (a: JobsBillingCard, b: JobsBillingCard) => {
                    const aFirstNameOrClientName = a.contactInformation?.firstName || a.name || "";
                    const bFirstNameOrClientName = b.contactInformation?.firstName || b.name || "";
                    const primaryComparison = compareString(aFirstNameOrClientName, bFirstNameOrClientName, dir);
                    return primaryComparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : primaryComparison;
                };
                break;
            case "LastName":
                sortFunction = (a: JobsBillingCard, b: JobsBillingCard) => {
                    const aLastName = a.contactInformation?.lastName || "";
                    const bLastName = b.contactInformation?.lastName || "";
                    const primaryComparison = compareString(aLastName, bLastName, dir);
                    return primaryComparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : primaryComparison;
                };
                break;
            case "DateReceived":
                sortFunction = this.createDateSortFunction<JobsBillingCard>(
                    (card) => card.documentsReceivedDate,
                    normalizedSortOrderValue,
                    (a, b) => this.compareJobsBillingFirstNameThenJobIdAscending(a, b),
                );
                break;
            case "DaysInStep":
                sortFunction =
                    normalizedSortOrderValue === "Ascending"
                        ? (a: JobsBillingCard, b: JobsBillingCard) => {
                              const aDaysSince = this.getJobsBillingDaysSince(a);
                              const bDaysSince = this.getJobsBillingDaysSince(b);
                              if (aDaysSince == null && bDaysSince == null) return this.compareJobsBillingFirstNameThenJobIdAscending(a, b);
                              if (aDaysSince == null) return -1;
                              if (bDaysSince == null) return 1;
                              const primaryComparison = aDaysSince.secondsSinceInStep - bDaysSince.secondsSinceInStep;
                              return primaryComparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : primaryComparison;
                          }
                        : (a: JobsBillingCard, b: JobsBillingCard) => {
                              const aDaysSince = this.getJobsBillingDaysSince(a);
                              const bDaysSince = this.getJobsBillingDaysSince(b);
                              if (aDaysSince == null && bDaysSince == null) return this.compareJobsBillingFirstNameThenJobIdAscending(a, b);
                              if (aDaysSince == null) return 1;
                              if (bDaysSince == null) return -1;
                              const primaryComparison = bDaysSince.secondsSinceInStep - aDaysSince.secondsSinceInStep;
                              return primaryComparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : primaryComparison;
                          };
                break;
            case "JobType": {
                const currentLang = this.helpersService.getLang() as SupportedLanguage;
                const fallbackLang = currentLang === "en-CA" ? "fr-CA" : "en-CA";
                const jobTypes = (await this.jobsAndBillingJobTypesService.getJobTypes()) ?? [];
                const sortedJobTypes = [...jobTypes].sort((a, b) => {
                    const aName = a.name?.[currentLang] ?? a.name?.[fallbackLang] ?? a.shortCode ?? "";
                    const bName = b.name?.[currentLang] ?? b.name?.[fallbackLang] ?? b.shortCode ?? "";
                    return compareString(aName, bName);
                });
                const jobTypeOrder = new Map(sortedJobTypes.map((jobType, index) => [jobType.id, index]));

                sortFunction = (a: JobsBillingCard, b: JobsBillingCard) => {
                    const aTypeOrder = jobTypeOrder.get(a.job.typeId) ?? Number.MAX_SAFE_INTEGER;
                    const bTypeOrder = jobTypeOrder.get(b.job.typeId) ?? Number.MAX_SAFE_INTEGER;
                    const comparison = normalizedSortOrderValue === "Ascending" ? aTypeOrder - bTypeOrder : bTypeOrder - aTypeOrder;
                    return comparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : comparison;
                };
                break;
            }
            case "PeriodEnded":
                sortFunction = this.createDateSortFunction<JobsBillingCard>(
                    (card) => card.job.periodEndDate,
                    normalizedSortOrderValue,
                    (a, b) => this.compareJobsBillingFirstNameThenJobIdAscending(a, b),
                );
                break;
            case "Priority":
                sortFunction = (a: JobsBillingCard, b: JobsBillingCard) => {
                    const aPriority = a.job.priority ?? "";
                    const bPriority = b.job.priority ?? "";
                    const primaryComparison = compareString(aPriority, bPriority, dir);
                    return primaryComparison === 0 ? this.compareJobsBillingFirstNameThenJobIdAscending(a, b) : primaryComparison;
                };
                break;
            case "TargetEndDate":
                sortFunction = this.createDateSortFunction<JobsBillingCard>(
                    (card) => card.job.targetEndDate,
                    normalizedSortOrderValue,
                    (a, b) => this.compareJobsBillingFirstNameThenJobIdAscending(a, b),
                );
                break;
            case "TargetStartDate":
                sortFunction = this.createDateSortFunction<JobsBillingCard>(
                    (card) => card.job.targetStartDate,
                    normalizedSortOrderValue,
                    (a, b) => this.compareJobsBillingFirstNameThenJobIdAscending(a, b),
                );
                break;
            case "CompletionDate":
                sortFunction = this.createDateSortFunction<JobsBillingCard>(
                    (card) => card.job.statusDate,
                    normalizedSortOrderValue,
                    (a, b) => this.compareJobsBillingFirstNameThenJobIdAscending(a, b),
                );
                break;
            default:
                throw new Error(`Sort value is not recognized : ${normalizedSortByValue}`);
        }

        return sortFunction;
    }

    getDaysSince(card: TaxprepT1Card) {
        return this.getCardFieldLabelsForStatus(card, card.return.statusId);
    }

    private getJobsBillingDaysSince(card: JobsBillingCard) {
        return this.getCardFieldLabelsForStatus(card, card.job.statusId);
    }

    private getCardFieldLabelsForStatus(card: TaxprepT1Card | JobsBillingCard, statusId: string) {
        const step = this.mappingOfStatusesService.getStepFromStatusId(statusId);
        if (!step) {
            return null;
        }

        return this.cardDisplayService.calcCardFieldLabels(
            card,
            step.Statuses.map((status) => status.TaxReturnStatusId),
        );
    }

    private createDateSortFunction<TCard>(
        dateExtractor: (card: TCard) => number | null | undefined,
        sortOrderValue: string | null,
        tieBreaker: (a: TCard, b: TCard) => number,
    ): (a: TCard, b: TCard) => number {
        if (sortOrderValue === "Ascending") {
            return (a: TCard, b: TCard) => {
                const aDate = dateExtractor(a);
                const bDate = dateExtractor(b);
                if (aDate == null && bDate == null) return tieBreaker(a, b);
                if (aDate == null) return -1;
                if (bDate == null) return 1;
                const dateComparison = aDate - bDate;
                if (dateComparison !== 0) return dateComparison;
                return tieBreaker(a, b);
            };
        }

        return (a: TCard, b: TCard) => {
            const aDate = dateExtractor(a);
            const bDate = dateExtractor(b);
            if (aDate == null && bDate == null) return tieBreaker(a, b);
            if (aDate == null) return 1;
            if (bDate == null) return -1;
            const dateComparison = bDate - aDate;
            if (dateComparison !== 0) return dateComparison;
            return tieBreaker(a, b);
        };
    }

    private compareT1FirstNamesAscending(a: TaxprepT1Card, b: TaxprepT1Card, dir: "asc" | "desc" = "asc"): number {
        const aFirstName = a.contactInformation?.firstName ?? "";
        const bFirstName = b.contactInformation?.firstName ?? "";
        const firstNameComparison = compareString(aFirstName, bFirstName, dir);
        if (firstNameComparison !== 0) return firstNameComparison;

        const aLastName = a.contactInformation?.lastName ?? "";
        const bLastName = b.contactInformation?.lastName ?? "";
        const lastNameComparison = compareString(aLastName, bLastName, dir);
        if (lastNameComparison !== 0) return lastNameComparison;

        // Fallback tiebreaker on card identifier to avoid card updates reinserting cards at the end (DASH-21980)
        const aCardId = a.cardId ?? "";
        const bCardId = b.cardId ?? "";
        return compareString(aCardId, bCardId, dir);
    }

    private compareJobsBillingFirstNameThenJobIdAscending(a: JobsBillingCard, b: JobsBillingCard, dir: "asc" | "desc" = "asc"): number {
        const aFirstNameOrClientName = a.contactInformation?.firstName ?? a.name ?? "";
        const bFirstNameOrClientName = b.contactInformation?.firstName ?? b.name ?? "";
        const firstNameComparison = compareString(aFirstNameOrClientName, bFirstNameOrClientName, dir);
        return firstNameComparison !== 0 ? firstNameComparison : this.compareJobsBillingJobIds(a, b, dir);
    }

    private compareJobsBillingJobIds(a: JobsBillingCard, b: JobsBillingCard, dir: "asc" | "desc"): number {
        return compareString(a.job.id, b.job.id, dir);
    }
}
